import GenericModal from "components/Modal";
import DeleteConfirmationModal from "components/Modal/DeleteConfirmationModal";
import { useEffect, useState } from "react";
import React from "react";
import { Row } from "components/DataTable";
import Distributors from "./Distributors";
import EditDistributor from "./EditDistributor";
import AddDistributors from "./AddDistribors";
import ViewDistributorInfo from "./ViewDistributorInfo";
import httpClient from "services/network/httpClient";
import { showToast } from "components/Toaster";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { sortByName } from "utility/sorting";

interface DistributorActionCellProps {
  row: object;
  onEdit: (row: any) => void;
  onView: (row: any) => void;
  onDelete: (row: any) => void;
}

const DistributorActionCell: React.FC<DistributorActionCellProps> = ({ row, onEdit, onView, onDelete }) => (
  <div className="flex space-x-2">
    <button
      type="button"
      onClick={() => onEdit(row)}
      className="px-2 py-2 rounded-lg cursor-pointer"
      aria-label="Edit distributor"
    >
      <FontAwesomeIcon icon={faEdit} />
    </button>
    <button
      type="button"
      onClick={() => onView(row)}
      className="px-2 py-2 rounded-lg cursor-pointer"
      aria-label="View distributor"
    >
      <FontAwesomeIcon icon={faEye} />
    </button>
    <button
      type="button"
      onClick={() => onDelete(row)}
      className="px-2 py-2 rounded-lg cursor-pointer"
      aria-label="Delete distributor"
    >
      <FontAwesomeIcon icon={faTrash} />
    </button>
  </div>
);
const DistributorsList = () => {
  const [isAdding, setIsAdding] = useState(false);
  const [data, setData] = useState<Row[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [vieData, setVieData] = useState<any>();
  const [isView, setIsView] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [deleteData, setDeleteData] = useState<any>();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    console.log("This is the bounce:", searchTerm);
    if (searchTerm === "" || searchTerm.trim() === "" || searchTerm === null || searchTerm === undefined || searchTerm.length <= 2) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPageNumber(1); // Reset to first page on new search
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const formatDistributorName = (text: string) =>
    text?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const getData = () => {
    setLoading(true);

    const searchQuery = debouncedSearch
      ? `distributor/search?term=${debouncedSearch}&page=${pageNumber}&size=${itemsPerPage}`
      : `distributor/all?page=${pageNumber}&size=${itemsPerPage}`;

    httpClient
      .get(searchQuery)
      .then((res: any) => {
        const contents = res?.value?.contents || [];

        sortByName(contents, "distributorName");

        const formattedData = contents.map((item: any) => ({
          ...item,
          distributorName: item.distributorName
            ? formatDistributorName(item.distributorName.trim())
            : "",
        }));

        setData(formattedData);
        setTotalItems(res.value.totalElements || formattedData.length);
      })
      .catch((error) => {
        console.log(error);
        showToast("Failed to fetch distributors", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };



  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, itemsPerPage, debouncedSearch]);

  const handleEdit = (row: any) => {
    setIsEdit(true);
    setEditData(row);
  };

  const handleView = (row: any) => {
    setIsView(true);
    setVieData(row);
  };

  const handleDelete = (row: any) => {
    setIsDelete(true);
    setDeleteData(row);
  };

  const columndata = [
    {
      key: "distributorName",
      label: "Distributor name",
      bold: true,
      formatter: (_: any, row: any) => {
        if (row.customerCode) {
          let name = row.distributorName || "";
          return `${row.customerCode}/${name}`.trim();
        } else {
          return row.distributorName || "";
        }
      },
      render: (_: any, row: any) => (
        <div className="flex flex-row item-center">
          <div>
            <div className="font-medium text-gray-800 dark:text-gray-200">{row.distributorName || 'Direct'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {row.customerCode && row.customerCode}
            </div>
          </div>
        </div>
      ),
    },

    {
      key: "phone",
      label: "phone",
      bold: true,
    },
    {
      key: "email",
      label: "Email",
      bold: true
    },

    {
      key: "contactPerson",
      label: "Contact Person",
      bold: true,
    },

    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: object) => (
        <DistributorActionCell row={row} onEdit={handleEdit} onView={handleView} onDelete={handleDelete} />
      ),
    },
  ];
  const handleAdd = () => {
    console.log("Handle Add");
    setIsAdding(true);
  };

  const handlePageChange = (newPage: number) => {
    console.log("Page changed to:", newPage);
    setPageNumber(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    console.log("Items per page changed to:", newItemsPerPage);
    setItemsPerPage(newItemsPerPage);
    setPageNumber(1); // Reset to first page when changing items per page
  };

  const deleteFuction = () => {
    httpClient
      .delete(`distributor/delete/${deleteData.id}?isDeleted=true`)
      .then((response) => {
        console.log("Delete distributore response");
        if (response.success === true) {
          getData();
          showToast("Deleted Distributor successfully", "success");
        } else {
          showToast(`${response.error.message}`, "error");
        }
      })
      .catch((error) => {
        console.info("Inside Error");
        showToast("Something went wrong", "error");
        console.log(error);
      })
      .finally(() => {
        console.info("you are in finally block");
        setIsDelete(false);
        getData();
      });
  };

  return (
    <div>
      <div className="">
        <div className="overflow-x-auto">
          <Distributors
            data={data}
            columns={columndata}
            onAdd={handleAdd}
            title="Distributor List"
            loading={loading}
            currentPage={pageNumber}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onSearchChange={setSearchTerm}
          ></Distributors>
        </div>
      </div>
      {isAdding && (
        <GenericModal
          isOpen={isAdding}
          onClose={() => setIsAdding(false)}
          header="Add Distributor"
          customWidth="md:max-w-lg"
        >
          <AddDistributors
            onClose={() => setIsAdding(false)}
            getData={getData}
          ></AddDistributors>
        </GenericModal>
      )}

      {isEdit && (
        <GenericModal
          isOpen={isEdit}
          onClose={() => setIsEdit(false)}
          header="Edit Distributor"
          size="md"
          customWidth="md:max-w-lg"
        >
          <EditDistributor
            onClose={() => setIsEdit(false)}
            data={editData}
            getData={getData}
          ></EditDistributor>
        </GenericModal>
      )}

      {isView && (
        <GenericModal
          isOpen={isView}
          onClose={() => setIsView(false)}
          header="Distributor Details"
          customWidth="md:max-w-lg"
        >
          <ViewDistributorInfo data={vieData}></ViewDistributorInfo>
        </GenericModal>
      )}
      {isDelete && (
        <DeleteConfirmationModal
          isOpen={isDelete}
          onClose={() => setIsDelete(false)}
          onConfirm={deleteFuction}
          entityName="Distributor"
          title="Delete Distributor"
        />
      )}
    </div>
  );
};
export default DistributorsList;
